import { useEffect, useRef } from 'react'

/** The element `index.html` defines and `SpaShellMiddleware` writes into. */
export const SERVER_BODY_ID = 'ssr'

/**
 * Hand the page over from the server's HTML to React, once, without a blank frame.
 *
 * `index.html` carries `<div id="ssr"><!--mha:body--></div>` immediately before `<div id="root">`,
 * and the host replaces the marker with a semantic fragment of the same page (`docs/tasks/B27`).
 * That fragment is what the visitor sees at first paint and it is the only thing a crawler that
 * does not run JavaScript will ever see, so it must stay on the page until React has the data to
 * draw the same sentences — which, for four of the five routes, is a lazy chunk *and* a request
 * away. `main.tsx` mounts into `#root` regardless; `styles/chrome.css` keeps React's tree out of
 * flow and unpainted for as long as a non-empty `#ssr` is there, so removing the server body moves
 * nothing and the measured layout shift is zero (R-810).
 *
 * The removal is deferred by one frame rather than done in the effect body. An effect runs after
 * React has committed but before the browser has painted that commit, so removing the node here
 * would delete the server's copy in the same frame that React's copy becomes visible only if the
 * two are laid out identically — and one `requestAnimationFrame` guarantees it instead: the
 * removal is applied in the frame's own style recalculation, together with React's first painted
 * ready state.
 *
 * `ready` is deliberately "settled", not "succeeded". An error page has to replace the server body
 * too: the host composes a static fragment when its own reads fail, and leaving both on the page
 * would show a visitor two different ledes about the same outage.
 *
 * It is a no-op when there is nothing to remove — the dev server and `vite preview` never fill the
 * marker, `?shell=1` is served with an empty body on purpose (it is what the service worker
 * precaches), and a client-side navigation happens long after the first page removed it.
 */
export function useServerBody(ready: boolean): void {
  const scheduled = useRef(false)

  useEffect(() => {
    if (!ready || scheduled.current) return
    scheduled.current = true
    // No cleanup that cancels this. React 19's StrictMode mounts every effect twice in
    // development, and a cleanup that called `cancelAnimationFrame` would cancel the only removal
    // this hook ever schedules — the second mount sees `scheduled` already set and does nothing,
    // and the server body would stay on top of the page for the life of the tab.
    requestAnimationFrame(() => {
      document.getElementById(SERVER_BODY_ID)?.remove()
    })
  }, [ready])
}
